/**
 * LLM Client abstraction for text-to-action pipeline.
 *
 * Proxies requests through the Python backend agents service.
 * Supports model selection and structured JSON output.
 *
 * @module gibbs/text-to-action/llmClient
 */

/**
 * @typedef {Object} LLMRequest
 * @property {Array<{role: string, content: string}>} messages
 * @property {string} model - Model identifier (e.g., 'gpt-3.5-turbo', 'o3-mini')
 * @property {boolean} [jsonMode=true] - Request JSON output format
 */

/**
 * @typedef {Object} LLMResponse
 * @property {string} content - Raw response content
 * @property {string} model - Model that generated the response
 * @property {Object|null} parsed - Parsed JSON if jsonMode was true
 * @property {string|null} error - Error message if request failed
 */

/**
 * Default endpoint for the agents command service.
 * Can be overridden via setEndpoint() for testing.
 */
let commandEndpoint = '/services/agents/command/';

/**
 * Override the LLM endpoint (for testing or alternative backends).
 *
 * @param {string} url
 */
export function setEndpoint(url) {
    commandEndpoint = url;
}

/** Default timeout in milliseconds. */
const DEFAULT_TIMEOUT_MS = 30000;

/** Maximum number of retry attempts for transient failures. */
const MAX_RETRIES = 3;

/** Base delay in ms for exponential backoff. */
const BASE_RETRY_DELAY_MS = 1000;

/**
 * Determine whether an HTTP status code is retryable (transient).
 * @param {number} status
 * @returns {boolean}
 */
function isRetryableStatus(status) {
    return status === 429 || status === 502 || status === 503 || status === 504;
}

/**
 * Send a request to the LLM via the agents backend.
 *
 * Includes a configurable timeout (default 30s) and exponential-backoff
 * retry for transient HTTP errors (429, 502, 503, 504).
 *
 * @param {LLMRequest} request
 * @param {Object} [options]
 * @param {number} [options.timeoutMs=30000] - Request timeout in milliseconds
 * @param {number} [options.maxRetries=3] - Maximum retry attempts
 * @returns {Promise<LLMResponse>}
 */
export async function sendLLMRequest(request, options = {}) {
    const { messages, model, jsonMode = true } = request;
    const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
    const maxRetries = options.maxRetries ?? MAX_RETRIES;

    const payload = {
        mode: 'action-plan',
        model,
        messages,
        response_format: jsonMode ? { type: 'json_object' } : undefined,
    };

    let lastError = null;

    // Retry loop with exponential backoff — sequential awaits are intentional.
    /* eslint-disable no-await-in-loop */
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), timeoutMs);

        try {
            const response = await fetch(commandEndpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(payload),
                signal: controller.signal,
            });

            clearTimeout(timer);

            if (!response.ok) {
                if (isRetryableStatus(response.status) && attempt < maxRetries) {
                    lastError = `HTTP ${response.status}: ${response.statusText}`;
                    await backoff(attempt);
                    continue;
                }
                return {
                    content: '',
                    model,
                    parsed: null,
                    error: `HTTP ${response.status}: ${response.statusText}`,
                };
            }

            const data = await response.json();

            if (data.error) {
                return {
                    content: data.response || '',
                    model,
                    parsed: null,
                    error: data.error,
                };
            }

            const content = data.response || '';
            let parsed = null;

            if (jsonMode) {
                try {
                    parsed = JSON.parse(content);
                } catch (_) {
                    // Will be handled by parseResponse
                }
            }

            return {
                content, model, parsed, error: null,
            };
        } catch (err) {
            clearTimeout(timer);
            const isTimeout = err.name === 'AbortError';
            lastError = isTimeout ? `Request timed out after ${timeoutMs}ms` : `Network error: ${err.message}`;

            if (attempt < maxRetries) {
                await backoff(attempt);
                continue;
            }
        }
    }
    /* eslint-enable no-await-in-loop */

    return {
        content: '',
        model,
        parsed: null,
        error: lastError || 'Request failed after retries',
    };
}

/**
 * Sleep with exponential backoff + jitter.
 * @param {number} attempt - Zero-based attempt number
 * @returns {Promise<void>}
 */
function backoff(attempt) {
    const delay = BASE_RETRY_DELAY_MS * (2 ** attempt) * (0.5 + Math.random() * 0.5);
    return new Promise((resolve) => setTimeout(resolve, delay));
}
